import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, ChevronUp, ChevronDown, MoreHorizontal, RefreshCw, AlertCircle, Plus, Edit, Save, X, Edit3, Info, ChevronRight } from 'lucide-react';
import { itemBinSizeGroupService } from '../../../../../services/itemBinSizeGroupService';
import { ItemBinSizeGroupDto, CreateItemBinSizeGroupRequest, UpdateItemBinSizeGroupRequest } from '../../../../../types/itemBinSizeGroup';
import { useAuth } from '../../../../../contexts/AuthContext';

interface ColumnFilter {
    [key: string]: string;
}

interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

interface SelectedRows {
    [key: string]: boolean;
}

interface ItemBinSizeGroupDataTableProps {
    selectedRows: SelectedRows;
    onRowSelection: (rowId: string, selected: boolean) => void;
    onBulkSelection: (rows: SelectedRows) => void;
    onDataChange: (data: ItemBinSizeGroupDto[]) => void;
    onOpenSidePanel: () => void;
    refreshTrigger?: number;
}

export const ItemBinSizeGroupDataTable: React.FC<ItemBinSizeGroupDataTableProps> = ({
    selectedRows,
    onRowSelection,
    onBulkSelection,
    onDataChange,
    onOpenSidePanel,
    refreshTrigger
}) => {
    const [columnFilters, setColumnFilters] = useState<ColumnFilter>({});
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [data, setData] = useState<ItemBinSizeGroupDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [editingData, setEditingData] = useState<Partial<ItemBinSizeGroupDto>>({});
    const [showInfo, setShowInfo] = useState<boolean>(false);

    const { user } = useAuth();

    const fields = ['itemBinSizeGroupID', 'description1', 'itemCount', 'binSizeConfigCount'];

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            loadData();
        }
    }, [refreshTrigger]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');

            let itemBinSizeGroups: ItemBinSizeGroupDto[] = [];

            try {
                itemBinSizeGroups = await itemBinSizeGroupService.getCurrent();
            } catch (err) {
                try {
                    itemBinSizeGroups = await itemBinSizeGroupService.getByCompanyNo(user?.companyNo || '001');
                } catch (err2) {
                    itemBinSizeGroups = [];
                }
            }

            setData(itemBinSizeGroups);
            onDataChange(itemBinSizeGroups);
        } catch (err: any) {
            setError(err.message || 'Feil ved lasting av data');
        } finally {
            setLoading(false);
        }
    };

    const CreateForm: React.FC<{ onClose: () => void; onSave: (request: CreateItemBinSizeGroupRequest) => void }> = ({ onClose, onSave }) => {
        const [formData, setFormData] = useState<CreateItemBinSizeGroupRequest>({
            companyNo: user?.companyNo || '001',
            description1: ''
        });

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            onSave(formData);
        };

        return (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Opprett ny vare-lokasjonsstørrelse-gruppe</h4>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Selskap</label>
                            <input
                                type="text"
                                value={formData.companyNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, companyNo: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Gruppe-ID (valgfri)</label>
                            <input
                                type="number"
                                value={formData.itemBinSizeGroupID || ''}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    itemBinSizeGroupID: e.target.value ? parseInt(e.target.value) : undefined
                                }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                placeholder="Auto-genereres hvis tom"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Beskrivelse (obligatorisk)</label>
                            <input
                                type="text"
                                value={formData.description1}
                                onChange={(e) => setFormData(prev => ({ ...prev, description1: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                                placeholder="Skriv beskrivelse..."
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="submit"
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Opprett
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                            Avbryt
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    const handleCreate = async (request: CreateItemBinSizeGroupRequest) => {
        try {
            setLoading(true);
            await itemBinSizeGroupService.create(request);
            setShowCreateForm(false);
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Feil ved opprettelse');
        } finally {
            setLoading(false);
        }
    };

    const startEdit = (row: ItemBinSizeGroupDto) => {
        setEditingRowId(row.id);
        setEditingData({ ...row });
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setEditingData({});
    };

    const saveEdit = async () => {
        if (!editingRowId || !editingData.description1 || !editingData.companyNo || editingData.itemBinSizeGroupID === undefined) {
            return;
        }

        try {
            setLoading(true);
            await itemBinSizeGroupService.update(
                editingData.companyNo,
                editingData.itemBinSizeGroupID,
                {
                    description1: editingData.description1
                }
            );

            setEditingRowId(null);
            setEditingData({});
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Feil ved lagring');
        } finally {
            setLoading(false);
        }
    };

    const updateEditingField = (field: keyof ItemBinSizeGroupDto, value: string | number) => {
        setEditingData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const filteredData = useMemo(() => {
        let filtered = data;

        if (globalFilter) {
            filtered = filtered.filter(row =>
                Object.values(row).some(value =>
                    value?.toString().toLowerCase().includes(globalFilter.toLowerCase())
                )
            );
        }

        Object.entries(columnFilters).forEach(([column, filterValue]) => {
            if (filterValue) {
                filtered = filtered.filter(row => {
                    const fieldValue = (row as any)[column];
                    return fieldValue?.toString().toLowerCase().includes(filterValue.toLowerCase());
                });
            }
        });

        return filtered;
    }, [data, columnFilters, globalFilter]);

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aValue = (a as any)[sortConfig.key];
            const bValue = (b as any)[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const handleSort = (key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleColumnFilter = (column: string, value: string) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    const handleSelectAll = (checked: boolean) => {
        const newSelection: SelectedRows = {};
        if (checked) {
            sortedData.forEach(row => {
                newSelection[row.id.toString()] = true;
            });
        }
        onBulkSelection(newSelection);
    };

    const isAllSelected = sortedData.length > 0 && sortedData.every(row => selectedRows[row.id.toString()]);
    const isIndeterminate = sortedData.some(row => selectedRows[row.id.toString()]) && !isAllSelected;

    const getFieldLabel = (field: string): string => {
        const labels: { [key: string]: string } = {
            'itemBinSizeGroupID': 'Gruppe-ID',
            'description1': 'Beskrivelse',
            'itemCount': 'Antall varer',
            'binSizeConfigCount': 'Antall størrelser'
        };
        return labels[field] || field;
    };

    if (loading && data.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-3" />
                    <span className="text-gray-600">Laster vare-lokasjonsstørrelse-grupper...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-center text-red-600">
                    <AlertCircle className="w-6 h-6 mr-3" />
                    <div>
                        <p className="font-medium">Feil ved lasting av data</p>
                        <p className="text-sm text-gray-600">{error}</p>
                        <button
                            onClick={loadData}
                            className="mt-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Prøv igjen
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-medium text-gray-900">Vare-lokasjonsstørrelse-grupper</h3>
                        <span className="text-sm text-gray-500">
                            ({sortedData.length} av {data.length} rader)
                        </span>
                        {loading && (
                            <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Søk i alle kolonner..."
                                value={globalFilter}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <button
                            onClick={() => setShowCreateForm(!showCreateForm)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Ny
                        </button>

                        <button
                            onClick={onOpenSidePanel}
                            disabled={Object.values(selectedRows).filter(Boolean).length === 0}
                            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                                Object.values(selectedRows).filter(Boolean).length > 0
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            <Edit3 className="w-4 h-4" />
                            Rediger ({Object.values(selectedRows).filter(Boolean).length})
                        </button>

                        <button
                            onClick={loadData}
                            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            <span className="text-sm">Oppdater</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Info-panel */}
            <div className="px-4 pt-3 pb-1">
                <button
                    onClick={() => setShowInfo(!showInfo)}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                    <Info className="w-3.5 h-3.5" />
                    <span>Hva er størrelsesgrupper?</span>
                    <ChevronRight className={`w-3 h-3 transition-transform ${showInfo ? 'rotate-90' : ''}`} />
                </button>
                {showInfo && (
                    <div className="mt-2 mb-1 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-2">
                        <p className="font-semibold">Hensikt</p>
                        <p className="text-xs leading-relaxed">
                            Størrelsesgrupper samler identiske bin-størrelse-konfigurasjoner slik at mange varer kan dele
                            ett felles oppsett. I stedet for å vedlikeholde tusenvis av like rader i ItemBinSize-tabellen,
                            definerer du oppsettet én gang i en gruppe og tilordner varene til den.
                        </p>
                        <p className="font-semibold mt-3">Oppløsningsprioritet</p>
                        <p className="text-xs leading-relaxed">
                            Systemet sjekker alltid i denne rekkefølgen: <strong>1) Direkte</strong> (vare-spesifikke ItemBinSize-rader)
                            → <strong>2) Gruppe</strong> (fra tilordnet størrelsesgruppe) → <strong>3) Fallback</strong> (standard 999999999).
                            Direkte rader overstyrer alltid gruppen.
                        </p>
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3">
                            <p className="font-semibold text-amber-800">Pass på</p>
                            <ul className="text-xs text-amber-700 mt-1 space-y-1 list-disc list-inside">
                                <li>Endring av en gruppe påvirker <strong>alle</strong> tilknyttede varer umiddelbart</li>
                                <li>Sjekk «Antall varer»-kolonnen før du endrer eller sletter en gruppe</li>
                                <li>Varer med egne direkte rader i ItemBinSize bruker ikke gruppen for de aktuelle bin-størrelsene</li>
                                <li>Sletting av en gruppe fjerner gruppetilordningen — varene faller tilbake til fallback-konfigurasjon</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {showCreateForm && (
                <CreateForm
                    onClose={() => setShowCreateForm(false)}
                    onSave={handleCreate}
                />
            )}

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="w-12 px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = isIndeterminate;
                                    }}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                />
                            </th>

                            {fields.map(field => (
                                <th key={field} className="px-4 py-3 text-left">
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => handleSort(field)}
                                            className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                        >
                                            <span>{getFieldLabel(field)}</span>
                                            {sortConfig.key === field ? (
                                                sortConfig.direction === 'asc' ? (
                                                    <ChevronUp className="w-3 h-3" />
                                                ) : (
                                                    <ChevronDown className="w-3 h-3" />
                                                )
                                            ) : (
                                                <div className="w-3 h-3" />
                                            )}
                                        </button>

                                        <input
                                            type="text"
                                            placeholder={`Filter ${getFieldLabel(field)}...`}
                                            value={columnFilters[field] || ''}
                                            onChange={(e) => handleColumnFilter(field, e.target.value)}
                                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </th>
                            ))}

                            <th className="w-16 px-4 py-3">
                                <span className="text-xs font-medium text-gray-700">Handlinger</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                        {sortedData.map(row => {
                            const isEditing = editingRowId === row.id;

                            return (
                                <tr
                                    key={row.id}
                                    className={`hover:bg-gray-50 transition-colors ${
                                        selectedRows[row.id.toString()] ? 'bg-blue-50' : ''
                                    } ${
                                        isEditing ? 'bg-yellow-50' : ''
                                    }`}
                                >
                                    <td className="px-4 py-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedRows[row.id.toString()] || false}
                                            onChange={(e) => onRowSelection(row.id.toString(), e.target.checked)}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            disabled={isEditing}
                                        />
                                    </td>

                                    {fields.map(field => (
                                        <td key={field} className="px-4 py-3">
                                            {isEditing && field === 'description1' ? (
                                                <input
                                                    type="text"
                                                    value={editingData[field as keyof ItemBinSizeGroupDto] as string || ''}
                                                    onChange={(e) => updateEditingField(field as keyof ItemBinSizeGroupDto, e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                                />
                                            ) : (
                                                <span className="text-sm text-gray-900">
                                                    {(row as any)[field]?.toString() || ''}
                                                </span>
                                            )}
                                        </td>
                                    ))}

                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1">
                                            {isEditing ? (
                                                <>
                                                    <button
                                                        onClick={saveEdit}
                                                        className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                                                        title="Lagre"
                                                        disabled={loading}
                                                    >
                                                        <Save className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                                        title="Avbryt"
                                                        disabled={loading}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => startEdit(row)}
                                                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                                                    title="Rediger"
                                                    disabled={loading}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {sortedData.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    <p>Ingen data funnet med gjeldende filtre</p>
                </div>
            ) : (
                <div className="p-4 border-t border-gray-200 text-sm text-gray-600">
                    Viser {sortedData.length} av {data.length} rader
                    {Object.values(selectedRows).filter(Boolean).length > 0 && (
                        <span className="ml-4">
                            • {Object.values(selectedRows).filter(Boolean).length} rader valgt
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};
